import React, { useState, useEffect } from "react";
import api from "../utils/api";

// Helper: Format Date object to API format: YYYY-MM-DD+HH:MM:SS
const formatDateForApi = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");
  return `${year}-${month}-${day}+${hours}:${minutes}:${seconds}`;
};

// Helper: Format Date object to local input string: YYYY-MM-DDTHH:mm
const formatDateToLocalInput = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const CustomExportModal = ({ onClose, chartTitle = "Chart" }) => {
  const [mode, setMode] = useState("hour");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [machine, setMachine] = useState("All");
  const [selectedMetric, setSelectedMetric] = useState("power_kw");
  const [machineOptions, setMachineOptions] = useState([{ name: "All", id: "All" }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const metricOptions = [
    { label: "Power (kW)", value: "power_kw" },
    { label: "Energy (kWh)", value: "energy" },
    { label: "Voltage (V)", value: "voltage" },
    { label: "Power Factor (pf)", value: "power_factor" },
    { label: "Current (A)", value: "current" },
    { label: "Frequency (Hz)", value: "frequency" },
    { label: "THD Voltage (%)", value: "THD_V" },
    { label: "THD Current (%)", value: "THD_I" },
    { label: "TAP (kW)", value: "TAP" },
  ];

  // Initialize dates: Default to last 24h
  useEffect(() => {
    const endNow = new Date();
    const startNow = new Date(endNow.getTime() - 24 * 60 * 60 * 1000);
    setEnd(formatDateToLocalInput(endNow));
    setStart(formatDateToLocalInput(startNow));
  }, []);

  // Fetch machine options from the hierarchy/metadata preloader
  useEffect(() => {
    const fetchMachines = async () => {
      try {
        const username = localStorage.getItem("username") || "demo";
        const now = new Date();
        const startStr = formatDateForApi(new Date(now.getTime() - 24 * 60 * 60 * 1000));
        const endStr = formatDateForApi(now);
        const url = `/machines_data?username=${encodeURIComponent(username)}&mode=hour&start=${startStr}&end=${endStr}&selected_machines=Usage`;

        const response = await api.get(url);
        const data = response.data?.data || response.data;
        if (data?.Hierarchy) {
          const list = [];
          Object.entries(data.Hierarchy).forEach(([id, obj]) => {
            const prettyName = obj.name || id;
            if (!list.some(item => item.name === prettyName)) {
              list.push({
                name: prettyName,
                id: id,
              });
            }
          });

          // Sort alphabetically by name
          list.sort((a, b) => a.name.localeCompare(b.name));

          // Set with "All" option prepended
          setMachineOptions([{ name: "All", id: "All" }, ...list]);
        } else {
          setMachineOptions([{ name: "All", id: "All" }]);
        }
      } catch (err) {
        console.error("Failed to preload machine options for custom export:", err);
        setMachineOptions([{ name: "All", id: "All" }]);
      }
    };
    fetchMachines();
  }, []);

  const validate = () => {
    if (!start || !end) {
      setError("Please select both a Start and an End date/time.");
      return null;
    }
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      setError("One or both of the entered dates are invalid.");
      return null;
    }
    if (startDate >= endDate) {
      setError("The Start date/time must be strictly before the End date/time.");
      return null;
    }

    const diffMs = endDate.getTime() - startDate.getTime();
    if (mode === "second") {
      const oneDayMs = 24 * 60 * 60 * 1000;
      if (diffMs > oneDayMs) {
        setError("For 'Second' mode, the selected range cannot exceed 24 hours.");
        return null;
      }
    }
    if (mode === "minute") {
      const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
      if (diffMs > thirtyDaysMs) {
        setError("For 'Minute' mode, the selected range cannot exceed 30 days.");
        return null;
      }
    }

    setError("");
    return { startDate, endDate };
  };

  const handleExport = async () => {
    const dates = validate();
    if (!dates) return;

    setLoading(true);
    setError("");

    try {
      const username = localStorage.getItem("username") || "demo";
      const startStr = formatDateForApi(dates.startDate);
      const endStr = formatDateForApi(dates.endDate);

      // Hit machines_data API with selected metric and target machine pretty name
      const params = new URLSearchParams({
        username,
        mode,
        start: startStr,
        end: endStr,
        metric: selectedMetric,
        selected_machines: machine === "All" ? "Usage" : machine,
      });

      const url = `/machines_data?${params.toString()}`;
      const response = await api.get(url);
      const raw = response.data?.data || response.data;

      if (!raw) {
        throw new Error("No data returned from the server.");
      }

      const timestamps = raw["Date & Time"] || [];
      if (!timestamps.length) {
        throw new Error("No timestamped data found in the selected range.");
      }

      // Reconstruct clean series map (supporting nested and legacy shapes)
      const series = {};
      if (raw.machines) {
        Object.entries(raw.machines).forEach(([id, machineObj]) => {
          const machineName = machineObj.name || id;
          if (machineObj.data) {
            Object.entries(machineObj.data).forEach(([metricName, rawValues]) => {
              const key = `${machineName}_${metricName}`;
              let vals = [];
              if (Array.isArray(rawValues)) {
                vals = rawValues;
              } else if (rawValues && typeof rawValues === "object") {
                vals = Object.values(rawValues);
              }
              series[key] = vals;
            });
          }
        });
      } else {
        Object.entries(raw).forEach(([key, rawValues]) => {
          if (key === "Date & Time") return;
          let vals = [];
          if (Array.isArray(rawValues)) {
            vals = rawValues;
          } else if (rawValues && typeof rawValues === "object") {
            vals = Object.values(rawValues);
          }
          series[key] = vals;
        });
      }

      // Filter series keys based on selected machine pretty name and its raw ID
      let seriesKeys = Object.keys(series);
      if (machine !== "All") {
        const matchedOpt = machineOptions.find((o) => o.name === machine);
        const machineId = matchedOpt ? matchedOpt.id : machine;

        seriesKeys = seriesKeys.filter((key) => {
          const lowerKey = key.toLowerCase();
          const lowerName = machine.toLowerCase();
          const lowerId = machineId.toLowerCase();

          return (
            key === machine ||
            key === machineId ||
            lowerKey.startsWith(lowerName + "_") ||
            lowerKey.startsWith(lowerId + "_") ||
            lowerKey.includes(lowerName) ||
            lowerKey.includes(lowerId)
          );
        });
      }

      if (!seriesKeys.length) {
        throw new Error(`No data series matched for the machine selection: "${machine}"`);
      }

      // Construct and download CSV
      const header = ["Date & Time", ...seriesKeys];
      const csvRows = [header];

      for (let i = 0; i < timestamps.length; i++) {
        const row = [timestamps[i]];
        seriesKeys.forEach((key) => {
          const val = series[key]?.[i];
          row.push(val !== undefined && val !== null ? val : "");
        });
        csvRows.push(row);
      }

      const csvContent = csvRows
        .map((row) =>
          row
            .map((cell) => {
              const escaped = String(cell).replace(/"/g, '""');
              return `"${escaped}"`;
            })
            .join(",")
        )
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const cleanTitle = chartTitle.replace(/\s+/g, "_");
      link.href = downloadUrl;
      link.setAttribute(
        "download",
        `${cleanTitle}_custom_${mode}_${new Date().getTime()}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      onClose();
    } catch (err) {
      console.error("Custom export error:", err);
      setError(err.message || "Failed to fetch and export custom data range.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={backdropStyle} onClick={onClose}>
      <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <h3 style={titleStyle}>Custom Data Export</h3>
          <button onClick={onClose} style={closeButtonStyle}>
            &times;
          </button>
        </div>

        {/* Error Notification */}
        {error && <div style={errorBannerStyle}>{error}</div>}

        {/* Inputs */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>Export Granularity (Mode)</label>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            style={selectStyle}
          >
            <option value="second">Second (Max 24h)</option>
            <option value="minute">Minute (Max 30d)</option>
            <option value="hour">Hour</option>
            <option value="day">Day</option>
          </select>
        </div>

        <div style={rowStyle}>
          <div style={{ ...formGroupStyle, flex: "1 1 180px", minWidth: "0" }}>
            <label style={labelStyle}>Start Date &amp; Time</label>
            <input
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ ...formGroupStyle, flex: "1 1 180px", minWidth: "0" }}>
            <label style={labelStyle}>End Date &amp; Time</label>
            <input
              type="datetime-local"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Target Machine</label>
          <select
            value={machine}
            onChange={(e) => setMachine(e.target.value)}
            style={selectStyle}
          >
            {machineOptions.map((opt) => (
              <option key={opt.name} value={opt.name}>
                {opt.name}
              </option>
            ))}
          </select>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Target Metric</label>
          <select
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            style={selectStyle}
          >
            {metricOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div style={actionContainerStyle}>
          <button onClick={onClose} disabled={loading} style={cancelButtonStyle}>
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={loading}
            style={loading ? exportingButtonStyle : submitButtonStyle}
          >
            {loading ? "Exporting CSV..." : "Export CSV"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Premium Styles
const backdropStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backdropFilter: "blur(8px)",
  backgroundColor: "rgba(15, 23, 42, 0.45)",
  animation: "fadeIn 0.2s ease-out",
  padding: "16px",
  boxSizing: "border-box",
};

const modalCardStyle = {
  background: "#ffffff",
  borderRadius: "16px",
  width: "100%",
  maxWidth: "480px",
  padding: "28px",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  border: "1px solid #f1f5f9",
  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  boxSizing: "border-box",
  maxHeight: "90vh",
  overflowY: "auto",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "20px",
  boxSizing: "border-box",
};

const titleStyle = {
  margin: 0,
  fontSize: "20px",
  fontWeight: 600,
  color: "#0f172a",
};

const closeButtonStyle = {
  background: "none",
  border: "none",
  fontSize: "24px",
  cursor: "pointer",
  color: "#94a3b8",
  transition: "color 0.2s",
};

const errorBannerStyle = {
  backgroundColor: "#fef2f2",
  color: "#dc2626",
  padding: "12px 16px",
  borderRadius: "8px",
  fontSize: "14px",
  marginBottom: "20px",
  border: "1px solid #fee2e2",
  lineHeight: 1.5,
  boxSizing: "border-box",
};

const formGroupStyle = {
  display: "flex",
  flexDirection: "column",
  marginBottom: "20px",
  boxSizing: "border-box",
};

const labelStyle = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#475569",
  marginBottom: "6px",
};

const selectStyle = {
  width: "100%",
  padding: "10px 14px",
  borderRadius: "8px",
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  fontSize: "14px",
  color: "#0f172a",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s, box-shadow 0.2s",
};

const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  borderRadius: "8px",
  border: "1px solid #cbd5e1",
  fontSize: "14px",
  color: "#0f172a",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s, box-shadow 0.2s",
};

const rowStyle = {
  display: "flex",
  gap: "16px",
  flexWrap: "wrap",
  width: "100%",
  boxSizing: "border-box",
};

const actionContainerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
  marginTop: "28px",
  boxSizing: "border-box",
};

const cancelButtonStyle = {
  padding: "10px 20px",
  borderRadius: "8px",
  border: "1px solid #cbd5e1",
  background: "transparent",
  fontSize: "14px",
  fontWeight: 500,
  color: "#475569",
  cursor: "pointer",
  transition: "background-color 0.2s",
};

const submitButtonStyle = {
  padding: "10px 20px",
  borderRadius: "8px",
  border: "none",
  background: "#2563eb",
  fontSize: "14px",
  fontWeight: 500,
  color: "#ffffff",
  cursor: "pointer",
  transition: "background-color 0.2s, transform 0.1s active",
};

const exportingButtonStyle = {
  ...submitButtonStyle,
  background: "#93c5fd",
  cursor: "not-allowed",
};

export default CustomExportModal;
